/**
 * Single source of truth for the wordmark and every line of copy on the page.
 * "Narriv" is a placeholder name — renaming is one edit to BRAND.name.
 * Copy is final (one-pager v10). Do not rewrite lines to fit a layout;
 * change the layout instead.
 */
export const BRAND = {
  name: "Narriv",
  /** Canonical origin, no trailing slash. Drives canonical/og:url + sitemap. */
  siteUrl: "https://narriv.net",
  contactEmail: "hello@narriv.net",
  footerLine: "analytical workspaces that learn, reuse and spend less.",
  metaDescription:
    "Narriv learns how your work actually gets done, then runs it on whichever model does each step best.",
};

export const CTA = {
  /** Rendered as "Drop us a line: <BRAND.contactEmail>", linking to mailto. */
  primaryLabel: "Drop us a line",
  secondaryLabel: "Read the thesis",
};

export const LANDING_PAGE = {
  /* S1 */
  hero: {
    eyebrowTagline: "A thesis on where AI value accumulates",
    headline: "The durable asset is not the model.",
    lede: "It's the accumulated knowledge of how your work actually gets done — and today almost everyone is throwing that away.",
    /* Stated once, plainly, so the rest of the page can describe the system in
     * present tense without hedging every claim individually. */
    status: "Narriv is in development. This page describes what we're building and the thinking behind it.",
  },

  /* S2 */
  problem: {
    heading: "Every request starts from zero.",
    body: "Most AI products treat every request as a new conversation. You ask, the work gets done, some memory may be retained but nothing is recorded about how it should be done next time. Organizations are generating process knowledge at scale and discarding all of it.",
  },

  /* S3 */
  workspace: {
    heading: "Start with a workspace.",
    body: "Point Narriv at a folder. That folder becomes an analytical workspace, and it is the only thing Narriv can see. It does not read your mail, reorganize your drive or move your calendar around.",
    pull: "The boundary is not a limitation — it is what makes the learning possible. A bounded set of related work is something a system can actually learn from. Everything else is noise.",
    scope: {
      insideLabel: "Narriv sees this",
      insidePath: "/clients/acme/",
      insideContents: ["decks", "models", "exports", "statements"],
      outsideLabel: "And nothing else",
      outsideItems: ["mail", "drive", "calendar", "CRM", "browser"],
    },
  },

  /* S4 */
  learns: {
    heading: "It learns the work you repeat.",
    body: "You arrive with a job in mind: rebuild this deck, compare these reports over time, make sense of these statements. Narriv does the work and records how it was actually done — the inputs, the steps, the checks, and the decisions that show up in the finished work but never in anyone's notes.",
  },

  /* S5 — centrepiece */
  workedExample: {
    eyebrow: "Worked example",
    heading: "“Update the deck.”",
    sub: "Give Narriv last quarter's deck and the files behind it. It works through the process, then runs it — routing each step to whatever that step actually needs.",
    diagram: {
      input: "“Update the deck.”",
      plan: "Plan",
      bandLabel: "Each step routed on its own merits",
      lanes: [
        { task: "Understand the deck", model: "Deep model", why: "judgment-heavy" },
        { task: "Refresh charts & tables", model: "Fast model", why: "known data shape" },
        { task: "Draft commentary", model: "Deep model", why: "judgment-heavy" },
      ],
      failover: "provider slow, costly or down → the step moves, the recipe holds",
      check: "Was it accepted?",
      remember: "Remember",
      loop: "Next quarter — same recipe, cheaper run",
    },
    caption: "The plan isn't meant to be static. Each run is evidence for what should be reused next time.",
  },

  /* S6 */
  leverage: {
    heading: "Reuse turns into leverage.",
    body: "When similar work appears, Narriv searches the workspace for a plan worth reusing and improves it instead of starting over. Recipes that prove useful get promoted and shared, and a folder shared with a team shares the know-how inside it. What began as one person's method becomes how the company does that job.",
  },

  /* S7 */
  cost: {
    heading: "And the same work keeps getting cheaper.",
    body: "Because Narriv knows what each step is for, it sends each one to the model that suits it: a frontier model for judgment, something faster and cheaper for the routine, and somewhere else entirely when a provider is slow or down. Every run adds to a record of which models produced work people actually accepted.",
    kicker: "Everyone can route. Almost nobody can route on evidence.",
  },

  /* S8 */
  objections: {
    heading: "The obvious questions.",
    items: [
      {
        question: "Isn't this just loop engineering?",
        answer:
          "Automation starts after someone has defined the workflow. Narriv starts before that. It does the work with you, captures what actually happened, and turns the processes worth repeating into reusable assets.",
      },
      {
        question: "Won't providers just do this?",
        answer:
          "Any of them can remember what you did. None can credibly tell you when their own model is the expensive way to do it.",
      },
      {
        question: "Why now?",
        answer:
          "Capability is converging while price and reliability diverge. There are more viable models than any company can evaluate by hand.",
      },
    ],
  },

  /* S9 */
  close: {
    quote:
      "Models become interchangeable. The knowledge of how your work actually gets done does not.",
  },
};
